import { useState, useEffect, useRef } from 'react';

interface UseCountUpOptions {
  /** Duration of the animation in milliseconds */
  duration?: number;
  /** Delay before starting the animation */
  delay?: number;
  /** Easing function - 'easeOut' slows down at the end */
  easing?: 'linear' | 'easeOut' | 'easeInOut';
}

/**
 * Hook that animates a number counting up from 0 to the target value.
 * Returns null while waiting for data (when target is undefined or during initial delay).
 */
export function useCountUp(
  target: number | undefined,
  options: UseCountUpOptions = {}
): number | null {
  const { duration = 1500, delay = 0, easing = 'easeOut' } = options;
  const [count, setCount] = useState<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const frameRef = useRef<number | undefined>(undefined);
  const previousTargetRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // If target is undefined, show null (loading state)
    if (target === undefined) {
      setCount(null);
      return;
    }

    // If target is 0, just show 0 immediately
    if (target === 0) {
      setCount(0);
      return;
    }

    // Only animate when target changes from undefined/different value
    const shouldAnimate = previousTargetRef.current !== target;
    previousTargetRef.current = target;

    if (!shouldAnimate) {
      return;
    }

    // Reset for new animation
    startTimeRef.current = null;

    const easingFunctions = {
      linear: (t: number) => t,
      easeOut: (t: number) => 1 - (1 - t) ** 3,
      easeInOut: (t: number) =>
        t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2,
    };

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime + delay;
      }

      const elapsed = currentTime - startTimeRef.current;

      // Still in delay period
      if (elapsed < 0) {
        setCount(null);
        frameRef.current = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFunctions[easing](progress);
      const currentCount = Math.round(easedProgress * target);

      setCount(currentCount);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [target, duration, delay, easing]);

  return count;
}
